import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Fonts } from "../../constants/AppEnums";
import AppDialog from "../../components/AppBase/AppDialog";
import { useDispatch, useSelector } from "react-redux";
import { showMessage } from "../../redux/actions";
import { theme } from "../../themes/theme";
import { RoleBundle } from "../../types/models/RoleBundle";
import { RoleBundleRequestBody } from "../../types/models/RoleBundleRequestBody";
import {
  createRoleBundle,
  deleteRoleBundle,
  getRoleBundles,
  updateRoleBundle,
} from "../../redux/actions/Role";
import { AppState } from "../../redux/store";
import { RoleState } from "../../types/models/RoleState";
import AppConfirmDialog from "../../components/AppBase/AppConfirmDialog";

interface RoleBundleDialogProp {
  isOpen: boolean;
  onClose: () => void;
  roleBundle?: RoleBundle;
}

const RoleBundleDialog: React.FC<RoleBundleDialogProp> = (prop) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formDisplayName, setFormDisplayName] = useState<string>("");
  const [formRoleIds, setFormRoleIds] = useState<string[]>([]);
  const [formDisplayNameError, setFormDisplayNameError] = useState<string>();
  const [formRoleIdsError, setFormRoleIdsError] = useState<string>();
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false);
  const [enableDeletion, setEnableDeletion] = useState<boolean>(false);

  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  useEffect(() => {
    if (prop.isOpen) {
      if (prop.roleBundle) {
        setFormDisplayName(prop.roleBundle.DisplayName);
        setFormRoleIds(prop.roleBundle.RoleIds);
        setEnableDeletion(true);
      } else {
        clearErrors();
        clearForm();
      }
    }
  }, [prop.isOpen]);

  const handleSubmit = () => {
    if (!validateForm()) return;

    const roleBundleRequest: RoleBundleRequestBody = {
      DisplayName: formDisplayName ?? "",
      RoleIds: formRoleIds ?? [],
    };
    setIsSubmitting(true);
    if (prop.roleBundle) {
      dispatch(
        updateRoleBundle(prop.roleBundle.Id, roleBundleRequest, (result) => {
          if (result) {
            // Refresh
            dispatch(
              getRoleBundles(() => {
                setIsSubmitting(false);
                dispatch(showMessage("The role bundle has been updated."));
                prop.onClose();
              })
            );
          } else {
            setIsSubmitting(false);
          }
        })
      );
    } else {
      dispatch(
        createRoleBundle(roleBundleRequest, (result) => {
          if (result) {
            // Refresh
            dispatch(
              getRoleBundles(() => {
                setIsSubmitting(false);
                dispatch(showMessage("The role bundle has been created."));
                prop.onClose();
              })
            );
          } else {
            setIsSubmitting(false);
          }
        })
      );
    }
  };

  const clearErrors = () => {
    setFormDisplayNameError(undefined);
    setFormRoleIdsError(undefined);
  };

  const clearForm = () => {
    setFormDisplayName("");
    setFormRoleIds([]);
    setEnableDeletion(false);
  };

  const closeDialog = () => {
    clearForm();
    clearErrors();
    prop.onClose();
  };

  const validateDisplayName = (value: string | undefined) => {
    if (value && value.length > 0 && value.length <= 256) {
      setFormDisplayNameError(undefined);
      return true;
    } else {
      setFormDisplayNameError("Please enter bundle name");
      return false;
    }
  };

  const validateRoles = (value: string[] | undefined) => {
    if (value && value.length > 0) {
      setFormRoleIdsError(undefined);
      return true;
    } else {
      setFormRoleIdsError("Please select one or more roles");
      return false;
    }
  };

  const validateForm = () => {
    const result1 = validateDisplayName(formDisplayName);
    const result2 = validateRoles(formRoleIds);

    return result1 && result2;
  };

  const handleChange = (field: string, value: string | string[] | undefined) => {
    switch (field) {
      case "DisplayName":
        setFormDisplayName(value as string);
        validateDisplayName(value as string);
        break;
      case "RoleIds":
        setFormRoleIds(value as string[]);
        validateRoles(value as string[]);
        break;
    }
  };

  const getRoleDisplayName = (roleId: string) => {
    const knownRole = roleState.knownRoles?.roles.find((role) => role.RoleId === roleId);
    return knownRole ? knownRole.DisplayName : roleId;
  };

  const handleDelete = () => {
    if (!prop.roleBundle) return;
    setOpenDeleteConfirm(false);
    setIsSubmitting(true);
    dispatch(
      deleteRoleBundle(prop.roleBundle?.Id, (result) => {
        if (result) {
          // Refresh
          dispatch(
            getRoleBundles(() => {
              setIsSubmitting(false);
              dispatch(showMessage("The role bundle has been deleted."));
              prop.onClose();
            })
          );
        } else {
          setIsSubmitting(false);
        }
      })
    );
  };

  return (
    <>
      <AppDialog
        sxStyle={{ height: "105%", top: "-15px" }}
        open={prop.isOpen}
        dividers
        disableBackDropOffClose
        title={
          <Typography sx={{ mb: 1, fontWeight: Fonts.SEMI_BOLD }}>
            {prop.roleBundle ? "Edit Role Bundle" : "Create New Role Bundle"}
          </Typography>
        }
        onClose={closeDialog}
      >
        <Box>
          {isSubmitting && (
            <Box
              sx={{
                position: "absolute",
                width: "90%",
                height: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size="3rem" sx={{ color: theme.palette.primary.main }} />
            </Box>
          )}
          <>
            <Box>
              <Box sx={{ px: 2, py: 1 }}>
                <TextField
                  required
                  name="DisplayName"
                  value={formDisplayName}
                  variant="outlined"
                  fullWidth
                  id="DisplayName"
                  label="Bundle Name"
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => handleChange("DisplayName", event.target.value)}
                  error={formDisplayNameError ? true : undefined}
                  helperText={formDisplayNameError}
                />
              </Box>
              <Box sx={{ p: 2, mt: 1, width: "100%" }}>
                <FormControl sx={{ width: "100%" }}>
                  <Autocomplete
                    multiple
                    value={formRoleIds}
                    options={
                      roleState.knownRoles?.roles
                        .filter((role) => role.Active)
                        .map<string>((role) => role.RoleId) ?? []
                    }
                    getOptionLabel={(option) => getRoleDisplayName(option)}
                    renderInput={(params) => (
                      <TextField sx={{ textTransform: "capitalize" }} {...params} label="Roles" />
                    )}
                    onChange={(e, value) => handleChange("RoleIds", value)}
                    renderTags={(values: string[], getTagProps) =>
                      values.map((value, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={value}
                          label={getRoleDisplayName(value)}
                          clickable
                          deleteIcon={
                            <CancelIcon onMouseDown={(event) => event.stopPropagation()} />
                          }
                        />
                      ))
                    }
                  />
                  {formRoleIdsError && (
                    <FormHelperText className="Mui-error">{formRoleIdsError}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                my: 2,
                px: 2,
              }}
            >
              {enableDeletion ? (
                <Button
                  color="error"
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                  }}
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => setOpenDeleteConfirm(true)}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Delete
                </Button>
              ) : (
                <Box />
              )}
              <Box>
                <Button
                  sx={{
                    minWidth: 100,
                    color: "white",
                    textTransform: "none",
                    mr: 2,
                  }}
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  {prop.roleBundle ? "Save Changes" : "Create Bundle"}
                </Button>
                <Button
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                  }}
                  color="primary"
                  variant="outlined"
                  onClick={() => closeDialog()}
                >
                  Cancel
                </Button>
              </Box>
            </Stack>
          </>
        </Box>
      </AppDialog>
      <AppConfirmDialog
        open={openDeleteConfirm}
        onDeny={() => setOpenDeleteConfirm(false)}
        onConfirm={() => handleDelete()}
        title="Are you sure you want to delete the role bundle?"
        dialogTitle="Delete Bundle?"
      ></AppConfirmDialog>
    </>
  );
};

export default RoleBundleDialog;
